"use client";

import { useState, useRef, useMemo, useCallback } from "react";
import flags from "react-phone-number-input/flags";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";
import { Select } from "@/components/ui/select";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { inputVariants, type InputVariantProps } from "@/components/ui/input";

type CountryCode = ReturnType<typeof getCountries>[number];

interface CountrySelectProps extends InputVariantProps {
  value: string;
  onChange: (countryCode: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

function FlagIcon({ country }: { country: string }) {
  const Flag = flags[country as keyof typeof flags];
  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20 shrink-0 [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={country} />}
    </span>
  );
}

const COUNTRIES = getCountries().map((code) => ({
  code,
  name: en[code] ?? code,
}));

export function CountrySelect({
  value,
  onChange,
  placeholder = "Select country...",
  disabled,
  variant = "default",
  size = "default",
  className,
}: CountrySelectProps) {
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(
    () =>
      COUNTRIES.filter(({ name, code }) =>
        `${name} ${code}`.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [searchValue],
  );

  const selected = useMemo(
    () => COUNTRIES.find((c) => c.code === value),
    [value],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open) {
        setSearchValue("");
        requestAnimationFrame(() => searchRef.current?.focus());
      }
    },
    [],
  );

  return (
    <Select.Root open={isOpen} modal onOpenChange={handleOpenChange}>
      <Select.Trigger
        size={size}
        variant={variant}
        disabled={disabled}
        className={cn("w-full cursor-pointer justify-between", className)}
      >
        {selected ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <FlagIcon country={selected.code} />
            <span className="text-sm text-heading truncate">{selected.name}</span>
          </div>
        ) : (
          <span className="text-sm text-contrast/30 flex-1 text-left">
            {placeholder}
          </span>
        )}
        <Select.Icon
          size={14}
          className={cn(
            "ml-0 opacity-50 transition-transform duration-300",
            disabled && "hidden",
            isOpen && "rotate-180",
          )}
        />
      </Select.Trigger>

      <Select.Content align="start" className="w-[320px]">
        <Select.Search
          ref={searchRef}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search country..."
        />

        <Select.List className="max-h-72">
          {filtered.length === 0 ? (
            <Select.ListEmpty>No country found.</Select.ListEmpty>
          ) : (
            filtered.map(({ code, name }) => (
              <Select.Item
                key={code}
                selected={code === value}
                onClick={() => {
                  onChange(code);
                  setIsOpen(false);
                }}
              >
                <FlagIcon country={code} />
                <Select.ItemLabel selected={code === value}>
                  {name}
                </Select.ItemLabel>
                {code === value && <Select.ItemCheck />}
              </Select.Item>
            ))
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}
